import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { relTime, fmtDate } from "@/lib/orgUtils";
import { FileSpreadsheet, FileText, Link2, Upload, Download, RotateCcw, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Network } from "lucide-react";

const CANON_OPTIONS = [
  ["employee_id", "Employee ID"], ["full_name", "Full Name"], ["designation", "Designation"],
  ["department", "Department"], ["manager_employee_id", "Manager Employee ID"], ["date_of_joining", "Date of Joining"],
  ["email", "Email"], ["location", "Location"], ["employment_type", "Employment Type"], ["photo_url", "Photo URL"],
  ["linkedin_url", "LinkedIn URL"], ["date_of_birth", "Date of Birth"], ["address", "Address"],
  ["highest_qualification", "Highest Qualification"], ["custom", "→ Custom field"], ["ignore", "✕ Ignore"],
];

export default function ImportPage() {
  const [params] = useSearchParams();
  const [step, setStep] = useState("choose");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("merge");
  const [gsheetUrl, setGsheetUrl] = useState("");
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [views, setViews] = useState([]);
  const [chartId, setChartId] = useState(params.get("chart") || "");
  const [sources, setSources] = useState({ sources: [], gsheet_api_configured: false });
  const [busy, setBusy] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const loadHistory = useCallback(() => {
    api.get("/import/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/import/sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/views").then((r) => setViews(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const editableCharts = views.filter((v) => v.can_edit);
  const chartName = Object.fromEntries(views.map((v) => [v.id, v.name]));

  const doPreview = async (file, url) => {
    if (!chartId) { toast.error("First choose which chart to import into."); return; }
    setBusy(true);
    const fd = new FormData();
    if (file) fd.append("file", file);
    if (url) fd.append("gsheet_url", url);
    fd.append("chart_id", chartId);
    try {
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
      setMapping(data.mapping);
      setStep("map");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { preview_id: preview.preview_id, mapping, mode });
      setResult(data);
      setStep("done");
      loadHistory();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const rollback = async (jobId) => {
    if (!window.confirm("Roll back the directory to the state before this import?")) return;
    try {
      const { data } = await api.post(`/import/rollback/${jobId}`);
      toast.success(`Rolled back — ${data.restored} employees restored`);
      loadHistory();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const refreshSheet = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/import/gsheet/refresh${chartId ? `?chart_id=${chartId}` : ""}`);
      toast.success(`Sheet refreshed — ${data.counts.ok} rows synced`);
      loadHistory();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const download = (path) => {
    const a = document.createElement("a");
    const token = localStorage.getItem("organa_token");
    fetch(`${API}/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        a.href = URL.createObjectURL(b);
        a.download = path.includes("error") ? "error_report.csv" : path.split("/").pop();
        a.click();
      });
  };

  return (
    <div className="h-full overflow-auto p-8" data-testid="import-page">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-black tracking-tight">Data Imports</h1>
        <p className="text-[#676a82] font-medium mt-1">Bring people into a chart from Google Sheets, Excel, or CSV — Organa does the rest.</p>

        {step === "choose" && (
          <>
            <div className="mt-6 p-4 rounded-2xl bg-white border-2 border-[#6161FF]/25 flex items-center gap-3 flex-wrap" data-testid="import-chart-picker">
              <span className="w-10 h-10 rounded-xl bg-[#6161FF]/10 flex items-center justify-center"><Network size={18} className="text-[#6161FF]" /></span>
              <div>
                <p className="font-black text-sm">Import into which chart?</p>
                <p className="text-xs text-[#676a82] font-medium">People always belong to a specific chart.</p>
              </div>
              <select data-testid="import-chart-select" value={chartId} onChange={(e) => setChartId(e.target.value)}
                className="ml-auto h-11 px-3 rounded-xl border-2 border-[#181B34]/10 text-sm font-bold bg-white min-w-64 focus:outline-none focus:border-[#6161FF]">
                <option value="">Choose a chart…</option>
                {editableCharts.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.headcount} people)</option>)}
              </select>
            </div>
            <div className={`mt-5 grid md:grid-cols-3 gap-4 ${!chartId ? "opacity-40 pointer-events-none" : ""}`}>
              <label className="group cursor-pointer rounded-2xl bg-white border-2 border-dashed border-[#181B34]/15 hover:border-[#00C875] p-6 flex flex-col items-center text-center gap-3" data-testid="upload-xlsx-card">
                <input type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && doPreview(e.target.files[0])} />
                <span className="w-12 h-12 rounded-xl bg-[#00C875]/12 flex items-center justify-center"><FileSpreadsheet size={22} className="text-[#00C875]" /></span>
                <p className="font-black">Excel upload</p>
                <p className="text-xs text-[#676a82] font-medium">.xlsx — first worksheet by default</p>
              </label>
              <label className="group cursor-pointer rounded-2xl bg-white border-2 border-dashed border-[#181B34]/15 hover:border-[#FDAB3D] p-6 flex flex-col items-center text-center gap-3" data-testid="upload-csv-card">
                <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && doPreview(e.target.files[0])} />
                <span className="w-12 h-12 rounded-xl bg-[#FDAB3D]/15 flex items-center justify-center"><FileText size={22} className="text-[#c97a10]" /></span>
                <p className="font-black">CSV upload</p>
                <p className="text-xs text-[#676a82] font-medium">UTF-8, comma-delimited, header row</p>
              </label>
              <div className="rounded-2xl bg-white border-2 border-dashed border-[#181B34]/15 p-6 flex flex-col items-center text-center gap-3" data-testid="gsheet-card">
                <span className="w-12 h-12 rounded-xl bg-[#579BFC]/15 flex items-center justify-center"><Link2 size={22} className="text-[#579BFC]" /></span>
                <p className="font-black">Google Sheet</p>
                <input data-testid="gsheet-url-input" value={gsheetUrl} onChange={(e) => setGsheetUrl(e.target.value)}
                  placeholder="Paste a shareable Sheets link"
                  className="w-full h-9 px-2.5 rounded-lg border border-[#181B34]/15 text-xs font-semibold focus:outline-none focus:border-[#6161FF]" />
                <button data-testid="gsheet-attach-btn" disabled={!gsheetUrl || busy} onClick={() => doPreview(null, gsheetUrl)}
                  className="w-full h-9 rounded-lg bg-[#579BFC] text-white text-xs font-bold disabled:opacity-40">Attach & preview</button>
                {!sources.gsheet_api_configured && (
                  <p className="text-[10px] text-[#946011] font-semibold bg-[#FDAB3D]/12 rounded-lg px-2 py-1">Sheets API not configured — using public CSV export fallback. Sheet must be "anyone with link can view".</p>
                )}
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <button data-testid="download-template-csv" onClick={() => download("/import/template.csv")} className="flex items-center gap-2 px-4 h-10 rounded-xl bg-white border border-[#181B34]/10 text-sm font-bold text-[#4b4e63] hover:border-[#6161FF]">
                <Download size={14} /> CSV template
              </button>
              <button data-testid="download-template-xlsx" onClick={() => download("/import/template.xlsx")} className="flex items-center gap-2 px-4 h-10 rounded-xl bg-white border border-[#181B34]/10 text-sm font-bold text-[#4b4e63] hover:border-[#6161FF]">
                <Download size={14} /> XLSX template
              </button>
              {chartId && sources.sources.some((s) => s.type === "gsheet" && s.chart_id === chartId) && (
                <button data-testid="refresh-gsheet-btn" onClick={refreshSheet} disabled={busy} className="flex items-center gap-2 px-4 h-10 rounded-xl bg-[#579BFC] text-white text-sm font-bold disabled:opacity-50">
                  <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh from Sheet
                </button>
              )}
            </div>
          </>
        )}

        {step === "map" && preview && (
          <div className="mt-8 bg-white rounded-2xl border border-[#181B34]/10 p-6 anim-in" data-testid="mapping-step">
            <h2 className="text-xl font-black tracking-tight">Map your columns</h2>
            <p className="text-sm text-[#676a82] font-medium mt-1">{preview.total_rows} rows detected → importing into <span className="font-bold text-[#6161FF]">{chartName[chartId]}</span>. We auto-matched what we could — adjust anything below.</p>
            <div className="mt-5 grid md:grid-cols-2 gap-3">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-3 p-2.5 rounded-xl bg-[#F6F7FB]">
                  <span className="flex-1 font-bold text-sm truncate">{h}</span>
                  <span className="text-[#9699AD]">→</span>
                  <select data-testid={`mapping-select-${h}`} value={mapping[h] || "ignore"} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                    className="h-9 px-2 rounded-lg border border-[#181B34]/15 text-sm font-semibold bg-white w-52">
                    {CANON_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">Import mode</label>
              <div className="mt-2 flex gap-2">
                <button data-testid="mode-merge" onClick={() => setMode("merge")} className={`px-4 h-10 rounded-xl text-sm font-bold ${mode === "merge" ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>Merge / Update (recommended)</button>
                <button data-testid="mode-replace" onClick={() => setMode("replace")} className={`px-4 h-10 rounded-xl text-sm font-bold ${mode === "replace" ? "bg-[#E2445C] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>Replace directory</button>
              </div>
            </div>
            <div className="mt-6 flex justify-between">
              <button onClick={() => setStep("choose")} className="px-4 h-11 rounded-xl font-bold text-[#4b4e63] hover:bg-[#F0F3FF]">← Back</button>
              <button data-testid="commit-import-btn" disabled={busy} onClick={commit}
                className="flex items-center gap-2 px-6 h-11 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold disabled:opacity-50">
                <Upload size={16} /> {busy ? "Validating & importing…" : "Validate & import"}
              </button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="mt-8 bg-white rounded-2xl border border-[#181B34]/10 p-6 anim-in" data-testid="import-result">
            <h2 className="text-xl font-black tracking-tight">Import complete</h2>
            <div className="mt-5 grid grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-[#00C875]/8 border-t-4 border-[#00C875]">
                <p className="text-3xl font-black">{result.counts.ok}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#00925a] flex items-center gap-1 mt-1"><CheckCircle2 size={12} /> Imported</p>
              </div>
              <div className="p-4 rounded-xl bg-[#FDAB3D]/10 border-t-4 border-[#FDAB3D]">
                <p className="text-3xl font-black">{result.counts.warnings}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#946011] flex items-center gap-1 mt-1"><AlertTriangle size={12} /> Warnings</p>
              </div>
              <div className="p-4 rounded-xl bg-[#E2445C]/8 border-t-4 border-[#E2445C]">
                <p className="text-3xl font-black">{result.counts.rejected}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#E2445C] flex items-center gap-1 mt-1"><XCircle size={12} /> Rejected</p>
              </div>
            </div>
            {result.absent_for_review?.length > 0 && (
              <div className="mt-4 p-4 rounded-xl bg-[#579BFC]/8 text-sm font-semibold text-[#2b6cb8]">
                {result.absent_for_review.length} employee(s) in the directory were absent from the file and held for review: {result.absent_for_review.map((a) => a.full_name).join(", ")}
              </div>
            )}
            {(result.counts.rejected > 0 || result.counts.warnings > 0) && (
              <button data-testid="download-error-report" onClick={() => download(`/import/jobs/${result.id}/error-report`)}
                className="mt-4 flex items-center gap-2 px-4 h-10 rounded-xl bg-[#181B34] text-white text-sm font-bold">
                <Download size={14} /> Download error report (CSV)
              </button>
            )}
            <div className="mt-6 flex gap-2">
              <button onClick={() => { setStep("choose"); setResult(null); setPreview(null); }} className="px-4 h-11 rounded-xl font-bold text-[#4b4e63] bg-[#F0F3FF]">Import more</button>
              <a href={`/chart/${chartId}`} className="px-6 h-11 rounded-xl bg-[#6161FF] text-white font-bold flex items-center">View chart →</a>
            </div>
          </div>
        )}

        <div className="mt-10">
          <h2 className="text-xl font-black tracking-tight">Import history</h2>
          <div className="mt-4 bg-white rounded-2xl border border-[#181B34]/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] border-b border-[#181B34]/8">
                  <th className="px-4 py-3">When</th><th className="px-4 py-3">Chart</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">OK / Warn / Rejected</th><th className="px-4 py-3">By</th><th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-[#181B34]/5" data-testid={`import-job-${j.id}`}>
                    <td className="px-4 py-2.5 font-semibold">{fmtDate(j.timestamp)} <span className="text-xs text-[#9699AD]">{relTime(j.timestamp)}</span></td>
                    <td className="px-4 py-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#6161FF]/10 text-[#6161FF]">{chartName[j.chart_id] || "—"}</span></td>
                    <td className="px-4 py-2.5 font-semibold text-[#4b4e63]">{j.source_type.toUpperCase()}</td>
                    <td className="px-4 py-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#6161FF]/10 text-[#6161FF]">{j.mode}</span></td>
                    <td className="px-4 py-2.5 font-bold">
                      <span className="text-[#00925a]">{j.counts.ok}</span> / <span className="text-[#c97a10]">{j.counts.warnings}</span> / <span className="text-[#E2445C]">{j.counts.rejected}</span>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-[#676a82]">{j.actor}</td>
                    <td className="px-4 py-2.5 text-right">
                      {(j.counts.rejected > 0 || j.counts.warnings > 0) && (
                        <button onClick={() => download(`/import/jobs/${j.id}/error-report`)} title="Error report" className="w-7 h-7 rounded-lg hover:bg-[#F0F3FF] text-[#676a82] inline-flex items-center justify-center"><Download size={13} /></button>
                      )}
                      <button data-testid={`rollback-job-${j.id}`} onClick={() => rollback(j.id)} title="Rollback" className="w-7 h-7 rounded-lg hover:bg-[#E2445C]/10 text-[#676a82] hover:text-[#E2445C] inline-flex items-center justify-center"><RotateCcw size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {jobs.length === 0 && <p className="p-6 text-center text-[#676a82] font-medium">No imports yet — your history will appear here.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
